import { useState, useRef } from 'react';
import { Button, ButtonGroup, Tooltip, Snackbar, Snack } from '@nike/eds';
import { Search, Null } from '@nike/nike-design-system-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLoaderData, useRevalidator } from 'react-router-dom';

import Loader from 'components/Loader';
import Input from 'components/Input';
import NicknameProductItem from 'components/NicknameProductItem';
import NicknameNewItem from '../../components/NickNameNewItemForm';
import { useAtom } from 'jotai';

import { QUERY_KEYS } from '../../constants';
import nicknameClientAPI, { ProductItem } from '../../clientAPI/nickname';
import propagate403Error from '../../utils/propagate403Error';
import { isExistScope } from '../../utils/authHelpers';

import {
  nickNameUpdateMessageAtom,
  nickNameUpdateHasErrorAtom,
} from '../../atoms/nickname';

export default function NickNameProductList() {
  const [updateMsg, setUpdateMsg] = useAtom(nickNameUpdateMessageAtom);
  const [updateHasError, setUpdateHasError] = useAtom(
    nickNameUpdateHasErrorAtom,
  );
  const queryClient = useQueryClient();
  const pageSize = 20;
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:nickname:write', jwt);
  const revalidator = useRevalidator();

  const productCodeInputRef = useRef<HTMLInputElement>(null);
  const styleCodeInputRef = useRef<HTMLInputElement>(null);
  const productIdInputRef = useRef<HTMLInputElement>(null);
  const nicknameInputRef = useRef<HTMLInputElement>(null);
  const [clearInput, setClearInput] = useState(false);
  const [searchProductCode, setSearchProductCode] = useState('');
  const [searchStyleCode, setSearchStyleCode] = useState('');
  const [searchProductId, setSearchProductId] = useState('');
  const [searchNickname, setSearchNickname] = useState('');
  const [page, setPage] = useState(1);
  const [displayNicknameProduct, setDisplayNicknameProduct] = useState(false);

  const {
    status,
    data: productList,
    error,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.NICKNAME_ALL_PRODUCTS,
      page,
      searchProductCode,
      searchStyleCode,
      searchProductId,
      searchNickname,
      displayNicknameProduct,
    ],
    queryFn: async () => {
      if (displayNicknameProduct) {
        return nicknameClientAPI.findProducts({
          currentPage: page,
          pageSize: pageSize,
          filterByNickname: '1',
        });
      }
      return nicknameClientAPI.findProducts({
        currentPage: page,
        pageSize: pageSize,
        productCode: searchProductCode,
        styleCode: searchStyleCode,
        productId: searchProductId,
        nickname: searchNickname,
      });
    },
    keepPreviousData: true,
    staleTime: Infinity,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  const mutation = useMutation({
    mutationFn: async ({
      productCode,
      nicknames,
      isDelete,
      nickname,
    }: {
      productCode: string;
      nicknames: string[];
      isDelete: boolean;
      nickname: string;
    }) => {
      if (isDelete) {
        await nicknameClientAPI.deleteNickname(productCode, nickname);
      } else {
        await nicknameClientAPI.updateNicknames(productCode, nicknames);
      }
      return { productCode, nicknames, isDelete, nickname };
    },
    onSuccess: () => {
      setUpdateHasError(false);
      setUpdateMsg('Update Success!');
      // Invalidate and refetch to get fresh data from server
      queryClient.invalidateQueries([QUERY_KEYS.NICKNAME_ALL_PRODUCTS]);
    },
    onError: (err: { status: number }) => {
      setUpdateHasError(true);
      setUpdateMsg('bad input!');
      err.status === 401 && revalidator.revalidate();
    },
  });

  const onCreateFn = (data: NicknameItemPayload) => {
    return mutation.mutateAsync(data);
  };

  const onSearch = () => {
    setSearchProductCode(productCodeInputRef?.current?.value.trim() || '');
    setSearchStyleCode(styleCodeInputRef?.current?.value.trim() || '');
    setSearchProductId(productIdInputRef?.current?.value.trim() || '');
    setSearchNickname(nicknameInputRef?.current?.value.trim() || '');
    setClearInput(false);
    setPage(1);
    setDisplayNicknameProduct(false);
  };

  const updateNickname = (
    productCode: string,
    nicknames: string[],
    isDelete: boolean,
    nickname: string,
  ) => {
    mutation.mutate({ productCode, nicknames, isDelete, nickname });
  };

  const resetSearch = () => {
    setDisplayNicknameProduct(false);
    setClearInput(true);
    setSearchProductCode('');
    setSearchStyleCode('');
    setSearchProductId('');
    setSearchNickname('');
    setPage(1);
  };

  const hasMoreData = productList?.length === pageSize;
  const noPagination = page === 1 && !hasMoreData;

  propagate403Error(error as keyable);

  return (
    <div className='tw-w-8/12 tw-mx-auto tw-py-10'>
      <p className='eds-type--display-5 tw-mb-4'>Product List</p>
      <div className='tw-flex tw-my-6 tw-items-center'>
        <Input
          placeholder='Product Code'
          className='tw-border-b tw-w-34 tw-mr-8'
          ref={productCodeInputRef}
          clear={clearInput}
          onSave={onSearch}
        />
        <Input
          placeholder='Style Code'
          className='tw-border-b tw-w-34 tw-mr-8'
          ref={styleCodeInputRef}
          clear={clearInput}
          onSave={onSearch}
        />
        <Input
          placeholder='Nickname'
          className='tw-border-b tw-w-34 tw-mr-14'
          ref={nicknameInputRef}
          clear={clearInput}
          onSave={onSearch}
        />
      </div>
      <div className='tw-flex tw-flex-row-reverse'>
        <ButtonGroup>
          <Button
            beforeSlot={<Search />}
            size='small'
            variant='primary'
            onClick={onSearch}
          >
            Search
          </Button>
          <Tooltip
            bodySlot={<p className='tw-text-sm'>Clear All</p>}
            placement={'top'}
            isDark
          >
            <Button
              beforeSlot={<Null />}
              size='small'
              variant='secondary'
              onClick={resetSearch}
            ></Button>
          </Tooltip>
        </ButtonGroup>
      </div>

      {status === 'loading' ? (
        <Loader />
      ) : (
        <>
          <div>
            {productList
              ?.filter(
                (product: ProductItem) => product?.nickName?.length ?? 0 > 0,
              )
              ?.map((product: ProductItem) => (
                <NicknameProductItem
                  key={`${product._id}`}
                  data={product}
                  onUpdateNickname={updateNickname.bind(
                    null,
                    product.productCode || product._id,
                  )}
                />
              ))}
            {productList?.length === 0 && page === 1 && (
              <h2 className='eds-type--display-5 tw-py-32 tw-text-center'>
                No Data available
              </h2>
            )}
          </div>

          <div className='tw-flex tw-items-center tw-justify-between tw-pb-40'>
            {!noPagination && (
              <p className='tw-text-sm tw-font-bold'>Current Page: {page}</p>
            )}
            <ButtonGroup>
              {page !== 1 && (
                <Button size='small' onClick={() => setPage(page - 1)}>
                  Previous Page
                </Button>
              )}
              {hasMoreData && (
                <Button size='small' onClick={() => setPage(page + 1)}>
                  Next Page
                </Button>
              )}
            </ButtonGroup>
          </div>
        </>
      )}
      {canWrite && <NicknameNewItem onCreateFn={onCreateFn} />}

      <Snackbar style={{ left: '90%', top: '90%' }}>
        {updateMsg && (
          <Snack
            autoDismissDuration={3000}
            id='nickname-update-error-notification'
            onDismiss={(id) => {
              setUpdateMsg('');
            }}
            status={updateHasError ? 'error' : 'success'}
          >
            {updateMsg}
          </Snack>
        )}
      </Snackbar>
    </div>
  );
}
